<template>
  <div>
    <slot name="label">
      <div class="d-flex justify-content-between align-items-center">
        <label class="font-semi-bold m-0 d-block">{{ title }}</label>
        <div class="d-flex align-items-center" style="gap: 4px">
          <button
            v-if="userBusinessClientId && !hideEditButton"
            type="button"
            class="sb-edit-btn"
            @click="onEditBusiness"
            title="Editar cliente"
          >
            <i class="el-icon-edit"></i>
          </button>
          <LinkPlusComponent
            onlyIcon
            @onClick="onAddBusiness"
            v-if="isAddBusiness"
          />
        </div>
      </div>
    </slot>

    <!-- Skeleton while loading initial client -->
    <div v-if="initialLoading" class="sb-skeleton"></div>

    <el-autocomplete
      v-else
      v-model="userBusinessClientName"
      :placeholder="placeholder"
      :fetch-suggestions="querySearch"
      :trigger-on-focus="false"
      @select="onSelect"
      suffix-icon="el-icon-search"
    >
      <template slot-scope="{ item }">
        <div class="value">{{ item.name }}</div>
      </template>
    </el-autocomplete>

    <el-dialog
      :visible.sync="dialogBusinessAdd"
      :fullscreen="true"
      :before-close="handleCloseBusinessAdd"
    >
      <BusinessAddComponent
        :showBackIcon="false"
        :isEmbedded="true"
        v-if="dialogBusinessAdd"
        @onSuccess="onSuccessBusinessAdd"
        @close="handleCloseBusinessAdd"
      />
    </el-dialog>
    <el-dialog
      :visible.sync="dialogBusinessEdit"
      :fullscreen="true"
      :before-close="handleCloseBusinessEdit"
    >
      <BusinessEditComponent
        v-if="dialogBusinessEdit && editBusinessId"
        :businessIdProp="editBusinessId"
        :isEmbedded="true"
        @onSaved="onBusinessEditSaved"
        @close="handleCloseBusinessEdit"
      />
    </el-dialog>
  </div>
</template>

<script>
import LinkPlusComponent from "@/modules/dashboard/components/LinkPlus";
import businessService from "../business/services/businessService";
import BusinessAddComponent from "../business/components/Add";
import BusinessEditComponent from "../business/components/Edit";

export default {
  name: "SelectBusinessV2Component",
  inject: {
    elFormItem: {
      // Element UI expone el form-item padre vía provide/inject con esta key
      from: "elFormItem",
      default: null
    }
  },
  data: () => ({
    searchBusiness: null,
    clients: [],
    contacts: [],
    dialogProductsAdd: false,
    userBusinessClientId: null,
    userBusinessClientName: null,
    dialogBusinessAdd: false,
    dialogBusinessEdit: false,
    editBusinessId: null,
    selectEdit: false,
    initialLoading: false
  }),
  methods: {
    goRouter(name, params = {}) {
      this.$router.push({ name, params });
    },
    async querySearch(queryString, cb) {
      if (this.setUserBusinessClientId != null && this.selectEdit) {
        this.selectEdit = false;
        this.userBusinessClientName = null;
        this.userBusinessClientId = null;
      }

      if (queryString.length >= 3) {
        this.searchBusiness = queryString;
        await this.getBusiness();
        cb(this.clients);
      }
    },
    onAddBusiness() {
      this.dialogBusinessAdd = true;
      this.userBusinessClientId = null;
      this.userBusinessClientName = null;
    },
    async onSelect(item) {
      if (item) {
        this.userBusinessClientId = item.id;
        this.userBusinessClientName = item.name;

        let dataClient = this.clients.find(client => client.id === item.id) || {
          id: item.id,
          name: item.name
        };
        let contacts = dataClient.contacts || [];

        // ── Emitir INMEDIATAMENTE con los datos disponibles ──────────────────
        // Esto evita la race condition en producción: el usuario puede hacer
        // click en "Buscar" antes de que termine el API call de contactos,
        // y el clientId ya estará disponible para el filtro.
        this.$emit("onChangeSelect", {
          client: { ...dataClient, contacts },
          contacts
        });
        this.$nextTick(() => {
          if (this.elFormItem) {
            this.elFormItem.$emit("el.form.change", item.id);
            this.elFormItem.$emit("el.form.blur", item.id);
          }
        });

        // ── Cargar contactos en background si no están disponibles ───────────
        // El segundo emit actualiza al componente padre con la data completa,
        // pero el filtro ya funciona desde el primer emit.
        if (!contacts.length) {
          try {
            const response = await businessService.getBusinessId(item.id);
            if (response.data && response.data.client) {
              dataClient = response.data.client;
              contacts = dataClient.contacts || [];
            } else if (response.data && response.data.contacts) {
              contacts = response.data.contacts;
            }
          } catch (e) {
            // silently ignore fetch error, contacts will be empty
          }

          this.contacts = contacts;
          const idx = this.clients.findIndex(c => c.id === item.id);
          if (idx !== -1) {
            this.$set(this.clients, idx, { ...dataClient, contacts });
          }

          // Re-emitir solo si hay datos nuevos (contactos cargados)
          if (contacts.length) {
            this.$emit("onChangeSelect", {
              client: { ...dataClient, contacts },
              contacts
            });
          }
        } else {
          this.contacts = contacts;
          const idx = this.clients.findIndex(c => c.id === item.id);
          if (idx !== -1) {
            this.$set(this.clients, idx, { ...dataClient, contacts });
          }
        }
      }
    },
    onSuccessBusinessAdd({ client }) {
      // Cierra el diálogo y selecciona el cliente recién creado — antes solo
      // se agregaba a la lista de sugerencias y el diálogo se quedaba
      // abierto, sin devolver al usuario al documento con nada seleccionado.
      this.clients = [client, ...this.clients];
      this.dialogBusinessAdd = false;
      this.onSelect(client);
    },
    handleCloseBusinessAdd() {
      this.dialogBusinessAdd = false;
    },
    onEditBusiness() {
      if (this.userBusinessClientId) {
        this.editBusinessId = this.userBusinessClientId;
        this.dialogBusinessEdit = true;
      }
    },
    handleCloseBusinessEdit() {
      this.dialogBusinessEdit = false;
      this.editBusinessId = null;
    },
    onBusinessEditSaved({ client }) {
      if (client) {
        this.userBusinessClientName = client.name;
        const idx = this.clients.findIndex(c => c.id === client.id);
        if (idx !== -1) {
          this.$set(this.clients, idx, client);
        }
        this.$emit("onChangeSelect", {
          client,
          contacts: client.contacts || this.contacts
        });
        this.$nextTick(() => {
          if (this.elFormItem) {
            this.elFormItem.$emit("el.form.change", client.id);
            this.elFormItem.$emit("el.form.blur", client.id);
          }
        });
      }
      this.handleCloseBusinessEdit();
    },
    async getBusiness() {
      await businessService
        .getBusiness({
          params: {
            name: this.searchBusiness,
            limit: 100
          }
        })
        .then(response => {
          if (response.data.clients) {
            this.clients = response.data.clients;

            if (this.setUserBusinessClientId != null && this.selectEdit) {
              const data = response.data.clients.find(
                f => f.id === this.setUserBusinessClientId
              );
              if (data) {
                this.userBusinessClientName = data.name;
              }
            }

            if (this.onlyProviders) {
              this.clients = response.data.clients.filter(client => {
                return client.contact_type == 2;
              });
            }
          }
        })
        .catch(() => {});
    },
    async loadInitialClient() {
      this.initialLoading = true;
      this.userBusinessClientId = this.setUserBusinessClientId;
      try {
        const response = await businessService.getBusinessId(
          this.setUserBusinessClientId
        );
        if (response.data && response.data.client) {
          this.userBusinessClientName = response.data.client.name;
          this.clients = [response.data.client];
        }
      } catch (e) {
        // silently ignore
      }
      this.initialLoading = false;
    }
  },
  components: {
    LinkPlusComponent,
    BusinessAddComponent,
    BusinessEditComponent
  },
  props: {
    title: {
      default: "Empresa"
    },
    placeholder: {
      default: "Buscar empresa"
    },
    autocomplete: {
      default: false,
      type: Boolean
    },
    select: {
      default: false,
      type: Boolean
    },
    isAddBusiness: {
      default: true,
      type: Boolean
    },
    onlyProviders: {
      default: false,
      type: Boolean
    },
    setUserBusinessClientId: {
      default: null
    },
    hideEditButton: {
      default: false,
      type: Boolean
    }
  },
  mounted() {
    if (this.setUserBusinessClientId != null) {
      this.loadInitialClient();
    }
  }
};
</script>

<style lang="scss" scoped>
.el-autocomplete {
  width: 100% !important;
}
.sb-edit-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: #909399;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
  &:hover {
    background: #f0f2f7;
    color: #31355c;
  }
}
.sb-skeleton {
  height: 40px;
  border-radius: 0;
  background: linear-gradient(90deg, #f0f2f5 25%, #e8eaed 50%, #f0f2f5 75%);
  background-size: 200% 100%;
  animation: sb-shimmer 1.2s ease-in-out infinite;
  border-bottom: 1px solid #e4eaf6;
}
@keyframes sb-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
